import { useState } from 'react';
import { Box, Button, Dialog, Divider, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ModalCard from '../ui/ModalCard';
import TextInput from '../ui/TextInput';
import { XIcon } from '../icons';
import { createOrganization } from '../../api/organizationsApi';

interface CreateOrganizationModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (name: string) => void;
}

export default function CreateOrganizationModal({ open, onClose, onCreated }: CreateOrganizationModalProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [error, setError] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  function handleClose() {
    onClose();
    setTimeout(() => {
      setName('');
      setError(false);
    }, 300);
  }

  async function handleSubmit() {
    const trimmed = name.trim();
    if (!trimmed) {
      setError(true);
      return;
    }
    setSubmitting(true);
    try {
      const result = await createOrganization({ name: trimmed });
      if (result.isSuccess) {
        onCreated(trimmed);
        handleClose();
      } else if (result.error) {
        setError(true);
      }
    } catch {
      // TODO(backend): show network error toast
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      slotProps={{
        paper: {
          elevation: 0,
          sx: { backgroundColor: 'transparent', overflow: 'visible', m: 2 },
        },
      }}
    >
      <ModalCard dialog>
        <Box sx={{ mt: -5, mx: -5, mb: -1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 5,
              py: 2,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {t('addOrganization.modalTitle')}
            </Typography>
            <IconButton onClick={handleClose} size="small" sx={{ color: 'text.secondary' }}>
              <XIcon size={16} />
            </IconButton>
          </Box>
          <Divider />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextInput
            id="organization-name"
            label={t('addOrganization.nameLabel')}
            placeholder={t('addOrganization.namePlaceholder')}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setError(false);
            }}
            error={error}
          />

          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              onClick={handleClose}
              fullWidth
              variant="outlined"
              sx={(theme) => ({
                py: 1.75,
                borderRadius: '100px',
                borderColor: theme.app.border,
                color: theme.app.foreground,
                fontWeight: 600,
                fontSize: 16,
                textTransform: 'none',
              })}
            >
              {t('addOrganization.cancel')}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              fullWidth
              sx={(theme) => ({
                py: 1.75,
                borderRadius: '100px',
                backgroundColor: theme.app.primary,
                color: theme.app.primaryForeground,
                fontWeight: 600,
                fontSize: 16,
                textTransform: 'none',
                '&:hover': { backgroundColor: theme.app.primaryHover },
              })}
            >
              {t('addOrganization.submit')}
            </Button>
          </Box>
        </Box>
      </ModalCard>
    </Dialog>
  );
}
